import { useNavigate } from "react-router-dom";
import { Archive, CircleCheck, FileKey, KeyRound, Loader2, Plus, Users } from "lucide-react";
import { useHealth, useSubscribers } from "@/hooks/queries";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { formatDate } from "@/lib/utils";

export function Dashboard() {
  const navigate = useNavigate();
  const health = useHealth();
  const list = useSubscribers({ page: 1, pageSize: 1 });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">
          Administrative tools for the PahCertificar Certificate Manager.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              API Health
            </p>
            <div className="mt-3 flex items-center gap-2">
              {health.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              ) : health.isSuccess ? (
                <CircleCheck className="h-5 w-5 text-emerald-500" />
              ) : (
                <span className="h-2 w-2 rounded-full bg-red-500" />
              )}
              <span className="text-sm font-medium text-slate-900">
                {health.isPending ? "Checking" : health.isSuccess ? "Operational" : "Down"}
              </span>
            </div>
            {health.data && (
              <p className="mt-1 text-[11px] text-slate-500 font-mono">
                {formatDate(health.data.timestamp)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Total subscribers
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Users className="h-5 w-5 text-brand-500" />
              <span className="text-2xl font-semibold text-slate-900">
                {list.isPending ? "…" : list.data?.total ?? "—"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              API version
            </p>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm font-medium text-slate-900">v2.0.0</span>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">Protected routes under /api/*</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-brand-600" />
              <p className="text-sm font-medium text-slate-900">Subscribers</p>
            </div>
            <p className="text-xs text-slate-500">
              Create and manage subscribers, representatives, certificate requests, and artifacts.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => navigate("/subscribers")}>
                Open list
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/subscribers/new")}
              >
                <Plus className="h-3.5 w-3.5" /> New subscriber
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-brand-600" />
              <p className="text-sm font-medium text-slate-900">Utilities</p>
            </div>
            <p className="text-xs text-slate-500">
              Standalone stateless CSR, PFX, and P12 generation — nothing is stored.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/utilities/csr")}
              >
                <KeyRound className="h-3.5 w-3.5" /> Generate CSR
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/utilities/pfx")}
              >
                <FileKey className="h-3.5 w-3.5" /> Generate PFX
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/utilities/p12")}
              >
                <Archive className="h-3.5 w-3.5" /> Generate P12
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
